/**
 * Dados MOCADOS dos cadastros da instalacao (regioes `showAdmin` e `listAdmin`
 * do prototipo SGW v2 — `docs/design-slices/showAdmin.html`).
 *
 * Nada aqui vai para a API: e material de tela para validar layout, densidade
 * e vocabulario em pt-BR antes de existir endpoint. Os numeros sao plausiveis
 * para uma instalacao de porte medio (≈40 pessoas internas).
 *
 * O prototipo tem CINCO modos de render (`showAdmin.html:3,35,69,94,115`) e a
 * secao escolhida no explorer decide qual entra. Por isso `AdminSection` e uma
 * uniao discriminada por `kind` em vez de uma tabela generica:
 *
 * | kind            | protótipo    | render                                  |
 * | --------------- | ------------ | --------------------------------------- |
 * | `members` | `admCollabs` | tabela com avatar, dot e icones          |
 * | `clients`       | `admClients` | cartoes com chips de dominio e switch    |
 * | `catalog`       | `admCatalog` | linhas com switch ativo/inativo          |
 * | `audit`         | `admAudit`   | feed quem/o que/onde/quando              |
 * | `other`         | `admOther`   | cartao "ainda nao detalhado"             |
 *
 * DIVERGENCIAS DELIBERADAS em relacao ao prototipo (`_shell.html:3623-3632`):
 *
 * 1. **"Áreas" existe aqui e nao no prototipo.** O usuario construiu areas no
 *    Kanban e elas sao usadas no produto (filtro de quadro, visibilidade,
 *    metricas), entao a secao fica. Ela entra como `catalog`, o mesmo padrao
 *    configuravel dos demais cadastros.
 * 2. **"Integrações e webhooks" e `catalog` aqui e `admOther` no prototipo.**
 *    A tela ja tinha as conexoes implementadas com situacao, contas e ultima
 *    sincronizacao; rebaixar isso para o cartao "ainda nao detalhado" seria
 *    perder funcao. O modo `other` continua existindo — ele atende a rota
 *    `/admin/<cadastro-inexistente>`.
 * 3. **A coluna "Papel" da tabela de membros saiu**: nao tem respaldo no
 *    prototipo (`showAdmin.html:13` lista Nome, E-mail, Área, Presença e
 *    Integrações). O papel continua no formulario, que o prototipo nao detalha.
 */
import type { StatusTone } from '@orbind/tokens'

/** Campo do modal de criar/editar — o modal e apenas visual, nao persiste. */
export type AdminFormField = {
  readonly key: string
  readonly label: string
  readonly kind: 'text' | 'textarea' | 'select' | 'date' | 'color'
  readonly required?: boolean
  readonly hint?: string
  readonly placeholder?: string
  readonly options?: readonly { readonly value: string; readonly label: string }[]
}

/** Selo de situacao/saude — rotulo + tom. Nunca so a cor. */
export type AdminBadge = {
  readonly label: string
  readonly tone: StatusTone
  readonly icon?: string
}

// ------------------------------------------------------------- membros

/** Presenca do membro. A cor e apoio: `label` e quem comunica. */
export type MemberPresence = {
  readonly label: string
  /** Cor do dot sobre o avatar — sempre acompanhada do rotulo na coluna. */
  readonly color: string
}

export type MemberRow = {
  readonly id: string
  readonly name: string
  /** Iniciais do avatar (o prototipo calcula; aqui vem pronto). */
  readonly initials: string
  readonly email: string
  readonly area: string
  readonly presence: MemberPresence
  /** Conta Google conectada — vira icone `fab fa-google`. */
  readonly google: boolean
  /** Caixa de e-mail integrada — vira icone `far fa-envelope`. */
  readonly mailbox: boolean
  readonly isActive: boolean
}

// ------------------------------------------------------------------ empresas

export type CompanyRow = {
  readonly id: string
  readonly name: string
  readonly code: string
  readonly owner: string
  /** Resumo curto que fecha a linha do cabecalho do cartao. */
  readonly counts: string
  /** Dominios correlacionados, SEM o "@" (a UI prefixa). */
  readonly domains: readonly string[]
  /** Notificar quando chegar e-mail deste cliente. */
  readonly notify: boolean
  readonly isActive: boolean
}

// ------------------------------------------------------------------ catalogos

export type CatalogRow = {
  readonly id: string
  readonly name: string
  /** Valor do campo 2 (traducao en-US, rotulo reverso ou codigo). */
  readonly translation: string
  readonly icon?: string
  /** Cor do registro — o nome dela viaja junto para nao comunicar so por cor. */
  readonly dot?: { readonly color: string; readonly label: string }
  /** Complemento da linha de apoio (uso, cobertura, SLA…). */
  readonly extra?: string
  /** Selo extra da linha (saude da conexao, categoria…). */
  readonly badge?: AdminBadge
  /** Registro semeado pelo sistema: nao pode ser excluido, so desativado. */
  readonly isSystem: boolean
  readonly isActive: boolean
}

// ------------------------------------------------------------------ auditoria

export type AuditEntry = {
  readonly id: string
  readonly who: string
  readonly what: string
  readonly where: string
  readonly time: string
}

// -------------------------------------------------------------------- secoes

type AdminSectionBase = {
  readonly id: string
  /** Rotulo curto do explorer. */
  readonly label: string
  readonly icon: string
  /** Titulo do conteudo (pode ser mais descritivo que o do explorer). */
  readonly title: string
  readonly description: string
  /** Linha de apoio do explorer: "7 áreas · 1 inativa". */
  readonly meta: string
  readonly createLabel: string
  readonly emptyDescription: string
}

export type AdminSection =
  | (AdminSectionBase & {
      readonly kind: 'members'
      readonly formFields: readonly AdminFormField[]
      readonly rows: readonly MemberRow[]
    })
  | (AdminSectionBase & {
      readonly kind: 'companies'
      readonly formFields: readonly AdminFormField[]
      readonly rows: readonly CompanyRow[]
    })
  | (AdminSectionBase & {
      readonly kind: 'catalog'
      /** Rotulo do campo 2 do formulario e da linha de apoio da linha. */
      readonly secondaryLabel: string
      readonly formFields: readonly AdminFormField[]
      readonly rows: readonly CatalogRow[]
    })
  | (AdminSectionBase & {
      readonly kind: 'audit'
      readonly entries: readonly AuditEntry[]
    })
  | (AdminSectionBase & { readonly kind: 'other' })

const PRESENCA = {
  disponivel: { label: 'Disponível', color: 'var(--ob-success)' },
  reuniao: { label: 'Em reunião', color: 'var(--ob-warning)' },
  foco: { label: 'Em foco', color: 'var(--ob-primary)' },
  ausente: { label: 'Ausente', color: 'var(--ob-warning-deep)' },
  offline: { label: 'Offline', color: 'var(--ob-text-muted)' },
} as const satisfies Record<string, MemberPresence>

const AREA_OPTIONS = [
  { value: 'engenharia', label: 'Engenharia de Produto' },
  { value: 'design', label: 'Design de Produto' },
  { value: 'dados', label: 'Dados e Analytics' },
  { value: 'atendimento', label: 'Atendimento ao Empresa' },
  { value: 'comercial', label: 'Comercial' },
  { value: 'financeiro', label: 'Financeiro' },
  { value: 'infra', label: 'Infraestrutura e SRE' },
] as const

/** Campo 2 de todo catalogo — `showAdmin.html:128` (`catEdF2Label`). */
function campoTraducao(label: string, placeholder: string): AdminFormField {
  return {
    key: 'translation',
    label,
    kind: 'text',
    placeholder,
    hint: 'Cada registro guarda a própria tradução: o bundle do idioma resolve a partir daqui.',
  }
}

// ----------------------------------------------------------- Membros

const members: AdminSection = {
  kind: 'members',
  id: 'members',
  // submenu "Membros" de "Controle de acesso" (pedido 2026-08-25); a tela
  // aberta segue sendo a de membros
  label: 'Membros',
  icon: 'fas fa-user-friends',
  title: 'Membros',
  description: 'Pessoas internas com acesso à plataforma, suas áreas e integrações.',
  meta: '9 pessoas · 2 sem integração',
  createLabel: 'Novo membro',
  emptyDescription: 'Nenhum membro cadastrado. Convide a primeira pessoa para começar a distribuir o trabalho.',
  formFields: [
    { key: 'name', label: 'Nome completo', kind: 'text', required: true, placeholder: 'Ex.: Marina Coelho' },
    { key: 'email', label: 'E-mail corporativo', kind: 'text', required: true, placeholder: 'nome@empresa.com.br' },
    { key: 'area', label: 'Área', kind: 'select', required: true, options: [...AREA_OPTIONS] },
    {
      key: 'role',
      label: 'Papel',
      kind: 'select',
      required: true,
      hint: 'Define o que a pessoa pode ver e alterar na instalação.',
      options: [
        { value: 'admin', label: 'Administrador' },
        { value: 'gestor', label: 'Gestor de área' },
        { value: 'membro', label: 'Membro' },
        { value: 'leitor', label: 'Leitor' },
      ],
    },
    { key: 'admission', label: 'Data de admissão', kind: 'date', hint: 'Usada no relatório de capacidade da área.' },
  ],
  rows: [
    {
      id: 'col-marina',
      name: 'Marina Coelho',
      initials: 'MC',
      email: 'marina.coelho@empresa.com.br',
      area: 'Engenharia de Produto',
      presence: PRESENCA.disponivel,
      google: true,
      mailbox: true,
      isActive: true,
    },
    {
      id: 'col-rafael',
      name: 'Rafael Bittencourt',
      initials: 'RB',
      email: 'rafael.bittencourt@empresa.com.br',
      area: 'Design de Produto',
      presence: PRESENCA.reuniao,
      google: true,
      mailbox: false,
      isActive: true,
    },
    {
      id: 'col-juliana',
      name: 'Juliana Prado',
      initials: 'JP',
      email: 'juliana.prado@empresa.com.br',
      area: 'Dados e Analytics',
      presence: PRESENCA.foco,
      google: true,
      mailbox: true,
      isActive: true,
    },
    {
      id: 'col-camila',
      name: 'Camila Nogueira',
      initials: 'CN',
      email: 'camila.nogueira@empresa.com.br',
      area: 'Atendimento ao Empresa',
      presence: PRESENCA.disponivel,
      google: false,
      mailbox: true,
      isActive: true,
    },
    {
      id: 'col-diego',
      name: 'Diego Fontenele',
      initials: 'DF',
      email: 'diego.fontenele@empresa.com.br',
      area: 'Comercial',
      presence: PRESENCA.ausente,
      google: true,
      mailbox: false,
      isActive: true,
    },
    {
      id: 'col-sonia',
      name: 'Sônia Vasques',
      initials: 'SV',
      email: 'sonia.vasques@empresa.com.br',
      area: 'Financeiro',
      presence: PRESENCA.offline,
      google: false,
      mailbox: false,
      isActive: true,
    },
    {
      id: 'col-tiago',
      name: 'Tiago Menezes',
      initials: 'TM',
      email: 'tiago.menezes@empresa.com.br',
      area: 'Engenharia de Produto',
      presence: PRESENCA.foco,
      google: true,
      mailbox: true,
      isActive: true,
    },
    {
      id: 'col-beatriz',
      name: 'Beatriz Sampaio',
      initials: 'BS',
      email: 'beatriz.sampaio@empresa.com.br',
      area: 'Atendimento ao Empresa',
      presence: PRESENCA.disponivel,
      google: false,
      mailbox: true,
      isActive: true,
    },
    {
      id: 'col-henrique',
      name: 'Henrique Salles',
      initials: 'HS',
      email: 'henrique.salles@empresa.com.br',
      area: 'Engenharia de Produto',
      presence: PRESENCA.offline,
      google: false,
      mailbox: false,
      isActive: false,
    },
  ],
}

// ---------------------------------------------------------------- Empresas

const companies: AdminSection = {
  kind: 'companies',
  id: 'companies',
  label: 'Empresas',
  icon: 'fas fa-building',
  title: 'Empresas',
  description:
    'Cadastro com domínios de e-mail correlacionados — a base que faz um e-mail recebido virar demanda do cliente certo.',
  meta: '6 empresas · domínios correlacionados',
  createLabel: 'Nova empresa',
  emptyDescription: 'Nenhuma empresa cadastrado. Cadastre o primeiro para correlacionar e-mails recebidos a demandas.',
  formFields: [
    { key: 'name', label: 'Razão social', kind: 'text', required: true, placeholder: 'Ex.: Banco Meridiano S.A.' },
    { key: 'code', label: 'Código', kind: 'text', required: true, placeholder: 'Ex.: MERID' },
    {
      key: 'domains',
      label: 'Domínios de e-mail',
      kind: 'text',
      placeholder: 'meridiano.com.br, meridiano.com',
      hint: 'Separe por vírgula. Também dá para acrescentar depois, pelo cartão do cliente.',
    },
    { key: 'owner', label: 'Responsável interno', kind: 'select', options: [...AREA_OPTIONS] },
    { key: 'notes', label: 'Observações', kind: 'textarea', placeholder: 'Contexto do contrato, contatos-chave…' },
  ],
  rows: [
    {
      id: 'cli-meridiano',
      name: 'Banco Meridiano S.A.',
      code: 'MERID',
      owner: 'Diego Fontenele',
      counts: '86 demandas no ano',
      domains: ['meridiano.com.br', 'bancomeridiano.com'],
      notify: true,
      isActive: true,
    },
    {
      id: 'cli-farmais',
      name: 'Rede Farmais',
      code: 'FARMA',
      owner: 'Camila Nogueira',
      counts: '54 demandas no ano',
      domains: ['farmais.com.br'],
      notify: true,
      isActive: true,
    },
    {
      id: 'cli-andrade',
      name: 'Transportes Andrade',
      code: 'ANDRA',
      owner: 'Diego Fontenele',
      counts: '41 demandas no ano',
      domains: ['transportesandrade.com.br', 'grupoandrade.com'],
      notify: false,
      isActive: true,
    },
    {
      id: 'cli-vega',
      name: 'Instituto Vega',
      code: 'VEGA',
      owner: 'Camila Nogueira',
      counts: '23 demandas no ano',
      domains: ['institutovega.org.br'],
      notify: true,
      isActive: true,
    },
    {
      id: 'cli-agrosul',
      name: 'Cooperativa AgroSul',
      code: 'AGSUL',
      owner: 'Juliana Prado',
      counts: '37 demandas no ano',
      domains: ['agrosul.coop.br', 'agrosuldigital.com.br'],
      notify: false,
      isActive: true,
    },
    {
      id: 'cli-lumen',
      name: 'Óptica Lumen',
      code: 'LUMEN',
      owner: 'Sônia Vasques',
      counts: 'Contrato encerrado em 03/2026',
      domains: ['opticalumen.com.br'],
      notify: false,
      isActive: false,
    },
  ],
}

// -------------------------------------------------------------------- Áreas

const areas: AdminSection = {
  kind: 'catalog',
  id: 'areas',
  label: 'Áreas',
  icon: 'fas fa-sitemap',
  title: 'Áreas',
  description:
    'Estrutura organizacional da instalação. A área define quem enxerga o quê nos quadros, nos filtros e nas métricas.',
  meta: '7 áreas · 1 inativa',
  createLabel: 'Nova área',
  emptyDescription: 'Nenhuma área cadastrada. Crie a primeira para organizar pessoas, quadros e demandas.',
  secondaryLabel: 'Tradução (en-US)',
  formFields: [
    { key: 'name', label: 'Nome da área', kind: 'text', required: true, placeholder: 'Ex.: Engenharia de Produto' },
    campoTraducao('Tradução (en-US)', 'Ex.: Product Engineering'),
    { key: 'code', label: 'Sigla', kind: 'text', placeholder: 'Ex.: ENG', hint: 'Usada nos códigos e nos relatórios.' },
    { key: 'lead', label: 'Responsável', kind: 'select', options: [...AREA_OPTIONS] },
    { key: 'description', label: 'Descrição', kind: 'textarea', placeholder: 'O que esta área faz' },
  ],
  rows: [
    {
      id: 'area-eng',
      name: 'Engenharia de Produto',
      translation: 'Product Engineering',
      icon: 'fas fa-code',
      extra: 'Marina Coelho · 18 pessoas · 214 Itens de Trabalho abertos',
      isSystem: false,
      isActive: true,
    },
    {
      id: 'area-design',
      name: 'Design de Produto',
      translation: 'Product Design',
      icon: 'fas fa-pen-nib',
      extra: 'Rafael Bittencourt · 6 pessoas · 48 Itens de Trabalho abertos',
      isSystem: false,
      isActive: true,
    },
    {
      id: 'area-dados',
      name: 'Dados e Analytics',
      translation: 'Data & Analytics',
      icon: 'fas fa-chart-line',
      extra: 'Juliana Prado · 5 pessoas · 33 Itens de Trabalho abertos',
      isSystem: false,
      isActive: true,
    },
    {
      id: 'area-atendimento',
      name: 'Atendimento ao Empresa',
      translation: 'Customer Support',
      icon: 'fas fa-headset',
      extra: 'Camila Nogueira · 9 pessoas · 127 Itens de Trabalho abertos',
      isSystem: false,
      isActive: true,
    },
    {
      id: 'area-comercial',
      name: 'Comercial',
      translation: 'Sales',
      icon: 'fas fa-handshake',
      extra: 'Diego Fontenele · 7 pessoas · 19 Itens de Trabalho abertos',
      isSystem: false,
      isActive: true,
    },
    {
      id: 'area-financeiro',
      name: 'Financeiro',
      translation: 'Finance',
      icon: 'fas fa-file-invoice-dollar',
      extra: 'Sônia Vasques · 4 pessoas · 12 Itens de Trabalho abertos',
      isSystem: false,
      isActive: true,
    },
    {
      id: 'area-infra',
      name: 'Infraestrutura e SRE',
      translation: 'Infrastructure & SRE',
      icon: 'fas fa-server',
      extra: 'Fundida com Engenharia de Produto em jan/2026',
      isSystem: false,
      isActive: false,
    },
  ],
}

// ------------------------------------------- Tipos de Item de Trabalho

const workItemTypes: AdminSection = {
  kind: 'catalog',
  id: 'work-item-types',
  label: 'Tipos de Item de Trabalho',
  icon: 'fas fa-shapes',
  title: 'Tipos de Item de Trabalho',
  description:
    'Tipos definem o comportamento do Item de Trabalho: ícone, cor e os fluxos a que ele pertence. Todos são cadastráveis; só não se exclui o que está em uso.',
  meta: 'Tipos cadastráveis · ícone e cor',
  createLabel: 'Novo tipo',
  emptyDescription:
    'Nenhum tipo de Item de Trabalho cadastrado. Crie o primeiro para começar a registrar o trabalho da equipe.',
  secondaryLabel: 'Tradução (en-US)',
  // Cadastro REAL (ver SECAO_REAL em AdminPage): os campos abaixo espelham o
  // que a API aceita. "Fluxo de status" saiu porque nao existe no modelo — o
  // fluxo e definido pelo quadro, nao pelo tipo.
  formFields: [
    { key: 'name', label: 'Nome do tipo', kind: 'text', required: true, placeholder: 'Ex.: História de usuário' },
    campoTraducao('Tradução (en-US)', 'Ex.: User story'),
    {
      key: 'prefix',
      label: 'Código',
      kind: 'text',
      required: true,
      placeholder: 'Ex.: HISTORIA',
      hint: 'Identificador estável do tipo, único no workspace.',
    },
    { key: 'icon', label: 'Ícone', kind: 'text', placeholder: 'fas fa-book' },
    { key: 'color', label: 'Cor', kind: 'color' },
  ],
  rows: [
    {
      id: 'wit-epico',
      name: 'Épico',
      translation: 'Epic',
      icon: 'fas fa-mountain',
      extra: 'Prefixo EPI · fluxo padrão · 24 itens',
      isSystem: false,
      isActive: true,
    },
    {
      id: 'wit-funcionalidade',
      name: 'Funcionalidade',
      translation: 'Feature',
      icon: 'fas fa-cube',
      extra: 'Prefixo FUN · fluxo padrão · 96 itens',
      isSystem: false,
      isActive: true,
    },
    {
      id: 'wit-historia',
      name: 'História de usuário',
      translation: 'User story',
      icon: 'fas fa-book-open',
      extra: 'Prefixo HIS · fluxo padrão · 203 itens',
      isSystem: false,
      isActive: true,
    },
    {
      id: 'wit-tarefa-tecnica',
      name: 'Tarefa técnica',
      translation: 'Technical task',
      icon: 'fas fa-wrench',
      extra: 'Prefixo TEC · fluxo padrão · 158 itens',
      isSystem: false,
      isActive: true,
    },
    {
      id: 'wit-bug',
      name: 'Defeito',
      translation: 'Bug',
      icon: 'fas fa-bug',
      extra: 'Prefixo DEF · fluxo de suporte · 74 itens',
      isSystem: false,
      isActive: true,
    },
    {
      id: 'wit-criterio',
      name: 'Critério de Aceite',
      translation: 'Acceptance criterion',
      icon: 'fas fa-clipboard-check',
      extra: 'Âncora estrutural · prefixo CRI · 412 itens',
      isSystem: true,
      isActive: true,
    },
    {
      id: 'wit-melhoria',
      name: 'Melhoria',
      translation: 'Improvement',
      icon: 'fas fa-arrow-up',
      extra: 'Prefixo MEL · fluxo padrão · 61 itens',
      isSystem: false,
      isActive: true,
    },
    {
      id: 'wit-spike',
      name: 'Investigação',
      translation: 'Spike',
      icon: 'fas fa-flask',
      extra: 'Prefixo INV · fluxo de descoberta · 3 itens',
      isSystem: false,
      isActive: false,
    },
  ],
}

// ------------------------------------------------------------------- Status

const statuses: AdminSection = {
  kind: 'catalog',
  id: 'work-item-statuses',
  label: 'Status',
  icon: 'fas fa-traffic-light',
  title: 'Status',
  description:
    'Status mapeáveis nas colunas dos quadros. A categoria alimenta as métricas de fluxo; a cor é apoio visual e sempre acompanha o rótulo.',
  meta: '9 status mapeáveis',
  createLabel: 'Novo status',
  emptyDescription: 'Nenhum status cadastrado. Crie ao menos um status inicial e um final para o fluxo funcionar.',
  secondaryLabel: 'Tradução (en-US)',
  formFields: [
    { key: 'name', label: 'Nome do status', kind: 'text', required: true, placeholder: 'Ex.: Em revisão' },
    campoTraducao('Tradução (en-US)', 'Ex.: In review'),
    {
      key: 'category',
      label: 'Categoria',
      kind: 'select',
      required: true,
      hint: 'Define se o item conta como pendente, em fluxo ou finalizado nas métricas.',
      options: [
        { value: 'nao-iniciado', label: 'Não iniciado' },
        { value: 'em-andamento', label: 'Em andamento' },
        { value: 'finalizado', label: 'Finalizado' },
      ],
    },
    { key: 'color', label: 'Cor', kind: 'color' },
    { key: 'description', label: 'Descrição', kind: 'textarea', placeholder: 'Quando um item deve estar neste status' },
  ],
  rows: [
    {
      id: 'st-backlog',
      name: 'Backlog',
      translation: 'Backlog',
      dot: { color: '#8A93B8', label: 'Cinza-azulado' },
      badge: { label: 'Não iniciado', tone: 'neutral', icon: 'far fa-circle' },
      extra: '318 itens',
      isSystem: true,
      isActive: true,
    },
    {
      id: 'st-analise',
      name: 'Em análise',
      translation: 'In analysis',
      dot: { color: '#6C5CE7', label: 'Roxo' },
      badge: { label: 'Não iniciado', tone: 'neutral', icon: 'far fa-circle' },
      extra: '46 itens',
      isSystem: false,
      isActive: true,
    },
    {
      id: 'st-pronto',
      name: 'Pronto para iniciar',
      translation: 'Ready to start',
      dot: { color: '#1B4DF5', label: 'Azul' },
      badge: { label: 'Não iniciado', tone: 'neutral', icon: 'far fa-circle' },
      extra: '52 itens',
      isSystem: false,
      isActive: true,
    },
    {
      id: 'st-andamento',
      name: 'Em andamento',
      translation: 'In progress',
      dot: { color: '#0E9AE0', label: 'Azul-claro' },
      badge: { label: 'Em andamento', tone: 'info', icon: 'fas fa-spinner' },
      extra: '38 itens',
      isSystem: true,
      isActive: true,
    },
    {
      id: 'st-revisao',
      name: 'Em revisão',
      translation: 'In review',
      dot: { color: '#E0A106', label: 'Âmbar' },
      badge: { label: 'Em andamento', tone: 'info', icon: 'fas fa-spinner' },
      extra: '21 itens',
      isSystem: false,
      isActive: true,
    },
    {
      id: 'st-bloqueado',
      name: 'Bloqueado',
      translation: 'Blocked',
      dot: { color: '#D93A3A', label: 'Vermelho' },
      badge: { label: 'Em andamento', tone: 'info', icon: 'fas fa-spinner' },
      extra: '9 itens · exige justificativa',
      isSystem: false,
      isActive: true,
    },
    {
      id: 'st-empresa',
      name: 'Aguardanda empresa',
      translation: 'Waiting for customer',
      dot: { color: '#E07A5F', label: 'Terracota' },
      badge: { label: 'Em andamento', tone: 'info', icon: 'fas fa-spinner' },
      extra: '14 itens · não conta no lead time',
      isSystem: false,
      isActive: true,
    },
    {
      id: 'st-concluido',
      name: 'Concluído',
      translation: 'Done',
      dot: { color: '#19B27B', label: 'Verde' },
      badge: { label: 'Finalizado', tone: 'success', icon: 'fas fa-check-circle' },
      extra: '1.284 itens',
      isSystem: true,
      isActive: true,
    },
    {
      id: 'st-cancelado',
      name: 'Cancelado',
      translation: 'Cancelled',
      dot: { color: '#6B76A3', label: 'Cinza' },
      badge: { label: 'Finalizado', tone: 'success', icon: 'fas fa-check-circle' },
      extra: '97 itens',
      isSystem: true,
      isActive: true,
    },
  ],
}

// -------------------------------------------------------------- Prioridades

const priorities: AdminSection = {
  kind: 'catalog',
  id: 'priorities',
  label: 'Prioridades',
  icon: 'fas fa-sort-amount-up',
  title: 'Prioridades',
  description:
    'Níveis de prioridade usados em Demandas e Itens de Trabalho. A ordem define a fila dos quadros e alimenta os alertas de prazo.',
  meta: '5 níveis',
  createLabel: 'Nova prioridade',
  emptyDescription: 'Nenhuma prioridade cadastrada. Crie ao menos uma para conseguir ordenar a fila de trabalho.',
  secondaryLabel: 'Tradução (en-US)',
  formFields: [
    { key: 'name', label: 'Nome da prioridade', kind: 'text', required: true, placeholder: 'Ex.: Alta' },
    campoTraducao('Tradução (en-US)', 'Ex.: High'),
    { key: 'order', label: 'Ordem de exibição', kind: 'text', required: true, placeholder: 'Ex.: 2' },
    { key: 'sla', label: 'SLA de resposta', kind: 'text', placeholder: 'Ex.: 8 horas úteis' },
    { key: 'color', label: 'Cor', kind: 'color' },
  ],
  rows: [
    {
      id: 'pr-critica',
      name: 'Crítica',
      translation: 'Critical',
      dot: { color: '#D93A3A', label: 'Vermelho' },
      extra: 'Ordem 1 · SLA 1 hora corrida · 6 itens',
      isSystem: true,
      isActive: true,
    },
    {
      id: 'pr-alta',
      name: 'Alta',
      translation: 'High',
      dot: { color: '#E0A106', label: 'Âmbar' },
      extra: 'Ordem 2 · SLA 8 horas úteis · 43 itens',
      isSystem: false,
      isActive: true,
    },
    {
      id: 'pr-media',
      name: 'Média',
      translation: 'Medium',
      dot: { color: '#1B4DF5', label: 'Azul' },
      extra: 'Ordem 3 · SLA 3 dias úteis · 187 itens',
      isSystem: true,
      isActive: true,
    },
    {
      id: 'pr-baixa',
      name: 'Baixa',
      translation: 'Low',
      dot: { color: '#19B27B', label: 'Verde' },
      extra: 'Ordem 4 · SLA 10 dias úteis · 96 itens',
      isSystem: false,
      isActive: true,
    },
    {
      id: 'pr-planejada',
      name: 'Planejada',
      translation: 'Planned',
      dot: { color: '#8A93B8', label: 'Cinza-azulado' },
      extra: 'Ordem 5 · sem SLA · 212 itens',
      isSystem: false,
      isActive: true,
    },
  ],
}

// --------------------------------------------------------- Tipos de relação

const relationTypes: AdminSection = {
  kind: 'catalog',
  id: 'relation-types',
  label: 'Tipos de relação',
  icon: 'fas fa-link',
  title: 'Tipos de relação',
  description:
    'Cada tipo tem um rótulo direto e um reverso, exibidos conforme o lado da relação. O reverso é aplicado automaticamente no item da outra ponta.',
  meta: '7 tipos · rótulo direto e reverso',
  createLabel: 'Novo tipo de relação',
  emptyDescription: 'Nenhum tipo de relação cadastrado. Crie o primeiro para poder ligar Itens de Trabalho entre si.',
  secondaryLabel: 'Rótulo reverso',
  formFields: [
    { key: 'name', label: 'Nome da relação', kind: 'text', required: true, placeholder: 'Ex.: Bloqueia' },
    campoTraducao('Rótulo reverso', 'Ex.: É bloqueado por'),
    { key: 'code', label: 'Código técnico', kind: 'text', required: true, placeholder: 'Ex.: BLOCKS' },
    { key: 'description', label: 'Descrição', kind: 'textarea', placeholder: 'Quando usar esta relação' },
  ],
  rows: [
    {
      id: 'rel-blocks',
      name: 'Bloqueia',
      translation: 'É bloqueado por',
      icon: 'fas fa-ban',
      extra: 'BLOCKS · 58 ligações',
      isSystem: false,
      isActive: true,
    },
    {
      id: 'rel-depends',
      name: 'Depende de',
      translation: 'É dependência de',
      icon: 'fas fa-project-diagram',
      extra: 'DEPENDS_ON · 134 ligações',
      isSystem: false,
      isActive: true,
    },
    {
      id: 'rel-acceptance',
      name: 'Critério de aceite de',
      translation: 'Possui critério de aceite',
      icon: 'fas fa-clipboard-check',
      extra: 'ACCEPTANCE_CRITERION_OF · âncora estrutural · 412 ligações',
      isSystem: true,
      isActive: true,
    },
    {
      id: 'rel-duplicates',
      name: 'Duplica',
      translation: 'É duplicado por',
      icon: 'fas fa-clone',
      extra: 'DUPLICATES · 27 ligações',
      isSystem: false,
      isActive: true,
    },
    {
      id: 'rel-relates',
      name: 'Relaciona-se com',
      translation: 'Relaciona-se com',
      icon: 'fas fa-share-alt',
      extra: 'RELATES_TO · 206 ligações',
      isSystem: true,
      isActive: true,
    },
    {
      id: 'rel-origin',
      name: 'Origina',
      translation: 'Originado por',
      icon: 'fas fa-code-branch',
      extra: 'ORIGINATES · 89 ligações',
      isSystem: false,
      isActive: true,
    },
    {
      id: 'rel-replaces',
      name: 'Substitui',
      translation: 'É substituído por',
      icon: 'fas fa-sync-alt',
      extra: 'REPLACES · 4 ligações',
      isSystem: false,
      isActive: false,
    },
  ],
}

// --------------------------------------------------- Idiomas e traduções

const locales: AdminSection = {
  kind: 'catalog',
  id: 'locales',
  label: 'Idiomas e traduções',
  icon: 'fas fa-globe',
  title: 'Idiomas e traduções',
  description:
    'O idioma padrão é usado como fallback quando falta tradução. O fallback completo é usuário → padrão da empresa → pt-BR → chave técnica.',
  meta: 'pt-BR padrão · fallback configurado',
  createLabel: 'Novo idioma',
  emptyDescription: 'Nenhum idioma habilitado além do pt-BR. Adicione um idioma para atender times de outros países.',
  secondaryLabel: 'Código',
  formFields: [
    { key: 'name', label: 'Nome do idioma', kind: 'text', required: true, placeholder: 'Ex.: Español (España)' },
    campoTraducao('Código', 'Ex.: es-ES'),
    {
      key: 'fallback',
      label: 'Idioma de fallback',
      kind: 'select',
      hint: 'Usado quando uma chave não tem tradução neste idioma.',
      options: [
        { value: 'pt-BR', label: 'Português (Brasil)' },
        { value: 'en-US', label: 'English (United States)' },
      ],
    },
  ],
  rows: [
    {
      id: 'loc-pt-br',
      name: 'Português (Brasil)',
      translation: 'pt-BR',
      icon: 'fas fa-flag',
      badge: { label: 'Padrão da empresa', tone: 'primary', icon: 'fas fa-star' },
      extra: '100% coberto · 1.842 chaves · 36 pessoas',
      isSystem: true,
      isActive: true,
    },
    {
      id: 'loc-en-us',
      name: 'English (United States)',
      translation: 'en-US',
      icon: 'fas fa-flag',
      extra: '94% coberto · 111 chaves pendentes · 4 pessoas',
      isSystem: false,
      isActive: true,
    },
    {
      id: 'loc-es-419',
      name: 'Español (LatAm)',
      translation: 'es-419',
      icon: 'fas fa-flag',
      extra: '68% coberto · 589 chaves pendentes · 1 pessoa',
      isSystem: false,
      isActive: true,
    },
    {
      id: 'loc-pt-pt',
      name: 'Português (Portugal)',
      translation: 'pt-PT',
      icon: 'fas fa-flag',
      extra: '41% coberto · herda de pt-BR o que falta',
      isSystem: false,
      isActive: true,
    },
    {
      id: 'loc-fr-fr',
      name: 'Français (France)',
      translation: 'fr-FR',
      icon: 'fas fa-flag',
      extra: '12% coberto · rascunho, aguardando o contrato europeu',
      isSystem: false,
      isActive: false,
    },
  ],
}

// ------------------------------------------------------------- Plugins

/**
 * Plugins (SPEC-20260926-2036). A tela é própria (features/admin/Plugins.tsx),
 * não um CRUD genérico: instalar é aceitar escopos, e isso não cabe em
 * `formFields`. A entrada aqui existe só para o item aparecer na lista.
 */
const plugins: AdminSection = {
  kind: 'other',
  id: 'plugins',
  label: 'Plugins',
  icon: 'fas fa-plug',
  title: 'Plugins',
  description:
    'Aplicativos de terceiros que estendem o Orbind. Rodam fora daqui e só acessam o que você autorizar.',
  meta: 'Instalar e desinstalar',
  // a tela própria não usa estes dois, mas a base os exige
  createLabel: 'Instalar plugin',
  emptyDescription: 'Nenhum plugin instalado. Os disponíveis são cadastrados no Console.',
}

/**
 * Conectores de IA (SPEC-20261103-1818). Tela própria
 * (features/admin/ConectoresDeIa.tsx): liberar pessoas e permissões não cabe
 * em `formFields`. A entrada aqui só põe o item na lista.
 */
const aiConnectors: AdminSection = {
  kind: 'other',
  id: 'ai-connectors',
  label: 'Conectores de IA',
  icon: 'fas fa-robot',
  title: 'Conectores de IA',
  description: 'Quem pode usar o Orbind de dentro do Claude, e com que permissões. O conector age como a pessoa.',
  meta: 'Liberar pessoas e permissões',
  createLabel: 'Liberar pessoa',
  emptyDescription: 'Ninguém liberado. Marque as pessoas que podem usar o Orbind pelo Claude.',
}

/**
 * Preencher responsáveis pela raiz (SPEC-20261103-2144). Tela própria
 * (features/admin/HerdarResponsavel.tsx): ensaio + aplicar, só para quem
 * administra o workspace (a API confere). Não é vendida por plano.
 */
const herdarResponsavel: AdminSection = {
  kind: 'other',
  id: 'herdar-responsavel',
  label: 'Responsáveis pela raiz',
  icon: 'fas fa-sitemap',
  title: 'Preencher responsáveis pela raiz',
  description: 'Itens sem responsável recebem o responsável do Item do topo da árvore deles.',
  meta: 'Ensaio e aplicação',
  createLabel: 'Ver o que mudaria',
  emptyDescription: 'Nada a preencher: todo Item vivo já tem responsável.',
}

// ------------------------------------------------ Integrações e webhooks

const integrations: AdminSection = {
  kind: 'catalog',
  id: 'integrations',
  label: 'Integrações e webhooks',
  icon: 'fas fa-plug',
  title: 'Integrações e webhooks',
  description:
    'Conexões com sistemas externos. Cada integração roda com uma conta de serviço própria e registra tudo na auditoria.',
  meta: '6 conexões · 1 com erro',
  createLabel: 'Nova integração',
  emptyDescription: 'Nenhuma integração conectada. Conecte a primeira para trazer e-mails, calendário e repositórios.',
  secondaryLabel: 'Tradução (en-US)',
  formFields: [
    { key: 'name', label: 'Nome da integração', kind: 'text', required: true, placeholder: 'Ex.: Google Workspace' },
    campoTraducao('Tradução (en-US)', 'Ex.: Google Workspace'),
    {
      key: 'kind',
      label: 'Tipo',
      kind: 'select',
      required: true,
      options: [
        { value: 'email', label: 'E-mail e calendário' },
        { value: 'chat', label: 'Mensageria' },
        { value: 'repo', label: 'Repositório de código' },
        { value: 'webhook', label: 'Webhook de saída' },
      ],
    },
    { key: 'endpoint', label: 'Endpoint', kind: 'text', placeholder: 'https://…' },
    { key: 'expires', label: 'Validade do token', kind: 'date', hint: 'Avisamos 15 dias antes de expirar.' },
  ],
  rows: [
    {
      id: 'int-google',
      name: 'Google Workspace',
      translation: 'Google Workspace',
      icon: 'fab fa-google',
      badge: { label: 'Conectada', tone: 'success', icon: 'fas fa-check-circle' },
      extra: 'E-mail e calendário · 24 contas · sincronizado há 4 minutos',
      isSystem: false,
      isActive: true,
    },
    {
      id: 'int-microsoft',
      name: 'Microsoft 365',
      translation: 'Microsoft 365',
      icon: 'fab fa-microsoft',
      badge: { label: 'Conectada', tone: 'success', icon: 'fas fa-check-circle' },
      extra: 'E-mail e calendário · 9 contas · sincronizado há 12 minutos',
      isSystem: false,
      isActive: true,
    },
    {
      id: 'int-slack',
      name: 'Slack',
      translation: 'Slack',
      icon: 'fab fa-slack',
      badge: { label: 'Conectada', tone: 'success', icon: 'fas fa-check-circle' },
      extra: 'Mensageria · 1 workspace · sincronizado há 1 minuto',
      isSystem: false,
      isActive: true,
    },
    {
      id: 'int-github',
      name: 'GitHub',
      translation: 'GitHub',
      icon: 'fab fa-github',
      badge: { label: 'Token expira em 9 dias', tone: 'warning', icon: 'fas fa-exclamation-triangle' },
      extra: 'Repositório de código · 12 repositórios · sincronizado há 3 horas',
      isSystem: false,
      isActive: true,
    },
    {
      id: 'int-jira',
      name: 'Jira Cloud',
      translation: 'Jira Cloud',
      icon: 'fab fa-jira',
      badge: { label: 'Falha de autenticação', tone: 'danger', icon: 'fas fa-times-circle' },
      extra: 'Espelho somente leitura · 1 projeto · sincronizado há 2 dias',
      isSystem: false,
      isActive: true,
    },
    {
      id: 'int-webhook',
      name: 'Webhook de saída — Datacenter',
      translation: 'Outbound webhook — Datacenter',
      icon: 'fas fa-satellite-dish',
      badge: { label: 'Desativada', tone: 'neutral', icon: 'fas fa-pause-circle' },
      extra: 'Publica mudanças de status no barramento interno · 1 endpoint',
      isSystem: false,
      isActive: false,
    },
  ],
}

// ---------------------------------------------------------- Feature toggles

/**
 * Secao REAL (SPEC-20260814-1144): a listagem e os formularios vivem em
 * `features/admin/FeatureToggles.tsx` e falam com `/feature-toggles` — este
 * registro existe so para a secao aparecer no explorer do admin.
 */
const featureToggles: AdminSection = {
  kind: 'other',
  id: 'feature-toggles',
  label: 'Feature toggles',
  icon: 'fas fa-toggle-on',
  title: 'Feature toggles',
  description: 'Ligue e desligue funcionalidades em tempo real, sem publicar uma nova versão.',
  meta: 'Release · kill-switch · plano',
  createLabel: 'Novo feature toggle',
  emptyDescription:
    'Crie o primeiro toggle para liberar uma funcionalidade aos poucos, desligar algo problemático sem deploy ou ligar módulos por plano.',
}

// ----------------------------------------------------- Fluxos de trabalho

/**
 * Secao REAL (SPEC-20260815-1204): tela em `features/admin/Flows.tsx`, fala
 * com `/flows` — este registro so poe a secao no explorer do admin.
 */
const flows: AdminSection = {
  kind: 'other',
  id: 'flows',
  label: 'Fluxos de trabalho',
  icon: 'fas fa-route',
  title: 'Fluxos de trabalho',
  description: 'Desenhe processos ligando etapas. As etapas são um catálogo único do workspace e as lanes dos quadros atuam sobre elas.',
  meta: 'Processos · etapas · desenho',
  createLabel: 'Novo fluxo',
  emptyDescription: 'Crie o primeiro fluxo e desenhe o processo ligando etapas.',
}

const camposComplementares: AdminSection = {
  kind: 'other',
  id: 'custom-fields',
  label: 'Campos complementares',
  icon: 'fas fa-list-alt',
  title: 'Campos complementares',
  description:
    'Crie campos próprios para os Itens de Trabalho por tipo — na barra de propriedades ou em abas — com obrigatório/opcional e visibilidade.',
  meta: 'Campos por tipo · propriedade/aba',
  createLabel: 'Novo campo',
  emptyDescription: 'Crie o primeiro campo complementar para um tipo de Item de Trabalho.',
}

const templates: AdminSection = {
  kind: 'other',
  id: 'templates',
  label: 'Templates',
  icon: 'fas fa-clone',
  title: 'Templates',
  description:
    'Modelos prontos: a cadeia inteira de um Item de Trabalho (filhos, checklists, campos) e listas de checklist reaproveitáveis.',
  meta: 'Item de Trabalho · checklist',
  createLabel: 'Novo template de checklist',
  emptyDescription: 'Salve um Item de Trabalho como template ou crie um template de checklist.',
}

// --------------------------------------------------------- Controle de acesso

/**
 * Secoes REAIS (SPEC-20260814-1444): telas em `features/admin/AccessControl.tsx`,
 * falam com `/access-control` — estes registros so as poem no explorer.
 * Ficam FORA do gating por plano (anti-lockout, como Feature toggles/Planos).
 */
const accessProfiles: AdminSection = {
  kind: 'other',
  id: 'access-profiles',
  // submenu de "Controle de acesso" (pedido 2026-08-25)
  label: 'Perfis',
  icon: 'fas fa-id-badge',
  title: 'Perfis de acesso',
  description: 'O que cada perfil pode: módulos, funcionalidades e ações.',
  meta: 'Permissões por perfil',
  createLabel: 'Novo perfil',
  emptyDescription: 'Crie perfis como “Gestor” ou “Operação” marcando o que cada um pode fazer.',
}

const accessGroups: AdminSection = {
  kind: 'other',
  id: 'access-groups',
  // submenu de "Controle de acesso" (pedido 2026-08-25)
  label: 'Grupos de usuários',
  icon: 'fas fa-users-cog',
  title: 'Grupos de acesso',
  description: 'Grupos reúnem usuários e concedem perfis a todos de uma vez.',
  meta: 'Times · perfis em lote',
  createLabel: 'Novo grupo',
  emptyDescription: 'Crie um grupo por time e conceda perfis em lote.',
}

// ------------------------------------------------------------------- Planos

/**
 * Secao REAL (SPEC-20260814-1333): tela em `features/admin/Plans.tsx`, fala
 * com `/plans` — este registro so poe a secao no explorer do admin.
 */
const plans: AdminSection = {
  kind: 'other',
  id: 'plans',
  label: 'Planos',
  icon: 'fas fa-cubes',
  title: 'Planos',
  description: 'Pacotes de módulos que um workspace contrata.',
  meta: 'Módulos por pacote · workspaces',
  createLabel: 'Novo plano',
  emptyDescription:
    'Crie o primeiro pacote de módulos — por exemplo, um plano Essencial com Demandas e Kanban, e um plano Pro com tudo.',
}

// ---------------------------------------------------------------- Auditoria

const audit: AdminSection = {
  kind: 'audit',
  id: 'audit',
  label: 'Auditoria',
  icon: 'fas fa-history',
  title: 'Auditoria',
  description:
    'Registro imutável de todas as alterações: quem, o quê, onde e quando. Alterações feitas por automações aparecem como “Automação”.',
  meta: 'Registro completo de alterações',
  createLabel: 'Registro imutável',
  emptyDescription: 'Nenhuma alteração registrada ainda. Assim que alguém mexer na instalação, aparece aqui.',
  entries: [
    {
      id: 'aud-1',
      who: 'Ana Martins',
      what: 'Alterou o tipo do IT-0187 de “Tarefa técnica” para “Defeito”',
      where: 'Itens de Trabalho',
      time: 'Hoje 10:41',
    },
    {
      id: 'aud-2',
      who: 'Automação',
      what: 'Preencheu a data real de início do IT-0182 ao entrar em “Em andamento”',
      where: 'Automações',
      time: 'Hoje 08:51',
    },
    {
      id: 'aud-3',
      who: 'Carlos Souza',
      what: 'Criou o cliente “Brasília Shopping” com o domínio bsbshopping.com.br',
      where: 'Administração · Empresas',
      time: 'Ontem 17:20',
    },
    {
      id: 'aud-4',
      who: 'Ana Martins',
      what: 'Habilitou “Exigir conclusão dos filhos” no IT-0186',
      where: 'Itens de Trabalho',
      time: 'Ontem 15:03',
    },
    {
      id: 'aud-5',
      who: 'João Ribeiro',
      what: 'Editou o status “Em revisão” (tradução en-US)',
      where: 'Administração · Status',
      time: 'Ontem 11:47',
    },
    {
      id: 'aud-6',
      who: 'Ana Martins',
      what: 'Removeu a relação IT-0186 → IT-0190 (“Bloqueia”)',
      where: 'Itens de Trabalho',
      time: '05/08 16:12',
    },
    {
      id: 'aud-7',
      who: 'Automação',
      what: 'Gerou o IT-0184 a partir do checklist da DMD-0047',
      where: 'Automações',
      time: '05/08 14:12',
    },
    {
      id: 'aud-8',
      who: 'Marina Coelho',
      what: 'Desativou a integração “Webhook de saída — Datacenter”',
      where: 'Administração · Integrações e webhooks',
      time: '04/08 16:55',
    },
    {
      id: 'aud-9',
      who: 'Marina Lima',
      what: 'Publicou o filtro salvo “Meus defeitos urgentes”',
      where: 'Filtros',
      time: '04/08 09:30',
    },
  ],
}

/** Nome exibido para alterações feitas por robô — sem avatar de pessoa. */
export const AUDIT_AUTOMACAO = 'Automação'

/**
 * Ordem do explorer.
 *
 * Espelha `_shell.html:3623-3632`, com "Áreas" acrescentada logo apos Empresas
 * (ver a divergencia 1 no topo do arquivo).
 */
export const ADMIN_SECTIONS: readonly AdminSection[] = [
  members,
  accessProfiles,
  accessGroups,
  // clients saiu daqui: virou modulo proprio do rail (/companies, 2026-08-25)
  areas,
  workItemTypes,
  statuses,
  priorities,
  relationTypes,
  flows,
  camposComplementares,
  templates,
  herdarResponsavel,
  locales,
  integrations,
  plugins,
  aiConnectors,
  // featureToggles e plans mudaram para o Console (super admin), 2026-08-26
  audit,
]
// mantidos referenciados para nao ficarem "orfaos" apos sairem da lista acima
void featureToggles
void plans

export function findAdminSection(id: string | undefined): AdminSection | null {
  if (!id) return null
  return ADMIN_SECTIONS.find((section) => section.id === id) ?? null
}
